import React, { useState, useEffect } from 'react';
import AntdProForm, {
  ProFormDigit,
  ProFormSelect,
  ProFormList,
  ProFormGroup,
} from '@ant-design/pro-form';
import { gql, useQuery } from '@apollo/client';
import fp from 'lodash/fp';
import PropTypes from 'prop-types';
import { findDuplicateByKey, getMin } from 'utils';
import ProForm from 'components/layout/ProForm';

const GET_OPTIONS_TAB_ROLE_HIERARCHY = gql`
  query getOptionsTabRoleHierarchy {
    roleHierarchyLevel: __type(name: "RoleHierarchyLevel") {
      enumValues {
        name
      }
    }
    roles(state: ALL) {
      id
      name
    }
  }
`;

const getDulicateSeq = fp.pipe(
  fp.get('roleHierarchy'),
  findDuplicateByKey('sequence'),
  fp.head,
  fp.get('sequence')
);
const getMinSeq = fp.pipe(fp.get('roleHierarchy'), getMin('sequence'));

const RoleHierarchyForm = ({ onFinish, initialValues, loading }) => {
  const { data, loading: loadingOptions } = useQuery(
    GET_OPTIONS_TAB_ROLE_HIERARCHY
  );
  const [form] = AntdProForm.useForm();
  const [nextSeq, setNextSeq] = useState(0);

  useEffect(() => {
    setNextSeq(
      fp.pipe(
        fp.get('roleHierarchy'),
        fp.maxBy('sequence'),
        fp.get('sequence')
      )(initialValues) + 1 || 0
    );
  }, [initialValues]);

  return (
    <ProForm
      loading={loading || loadingOptions}
      form={form}
      onFinish={onFinish}
      initialValues={initialValues}
      backPage="/system-setting/project-categories"
      onValuesChange={() => {
        const values = form.getFieldsValue();
        setNextSeq(
          fp.pipe(
            fp.get('roleHierarchy'),
            fp.maxBy('sequence'),
            fp.get('sequence')
          )(values) + 1
        );
      }}
    >
      <ProFormList
        name="roleHierarchy"
        creatorButtonProps={{
          creatorButtonText: 'Add',
          position: 'bottom',
          type: 'dashed',
        }}
        copyIconProps={false}
        deleteIconProps={{
          tooltipText: 'delete',
        }}
        creatorRecord={{
          sequence: nextSeq,
        }}
      >
        <ProFormGroup>
          <ProFormDigit
            label="Sequence"
            name="sequence"
            width="sm"
            placeholder="sequence"
            min={0}
            rules={[
              {
                required: true,
                message: 'please enter',
              },
              ({ getFieldsValue }) => ({
                validator(_, value) {
                  const values = getFieldsValue();
                  const minseq = getMinSeq(values);

                  if (minseq !== 0) {
                    return Promise.reject(
                      new Error('sequence have to start with 0')
                    );
                  }
                  if (getDulicateSeq(values) === value) {
                    return Promise.reject(
                      new Error('cannot have the duplicate sequence')
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          />
          <ProFormSelect
            label="Role"
            name="role"
            width="sm"
            placeholder="role"
            valueEnum={data?.roles?.reduce((acc, { id, name }) => {
              acc[id] = name;
              return acc;
            }, {})}
            rules={[
              {
                required: true,
                message: 'please select',
              },
            ]}
          />
          <ProFormSelect
            label="Level"
            name="level"
            width="sm"
            placeholder="level"
            valueEnum={data?.roleHierarchyLevel?.enumValues.reduce(
              (acc, { name }) => {
                acc[name] = name;
                return acc;
              },
              {}
            )}
            rules={[
              {
                required: true,
                message: 'please select',
              },
            ]}
          />
        </ProFormGroup>
      </ProFormList>
    </ProForm>
  );
};

RoleHierarchyForm.defaultProps = {
  initialValues: { roleHierarchy: [] },
  loading: false,
};

RoleHierarchyForm.propTypes = {
  onFinish: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    roleHierarchy: PropTypes.arrayOf(
      PropTypes.shape({
        role: PropTypes.string.isRequired,
        sequence: PropTypes.number.isRequired,
        level: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
  loading: PropTypes.bool.isRequired,
};

export default RoleHierarchyForm;
